/**
 * ChannelSection (TASK-233 Phase 4 — extracted from ChannelsPanel)
 *
 * One isolated, collapsible channel block driven by a `config_schema`. It owns
 * its own `enabled` + `values` state so saving channel A cannot affect channel
 * B, and persists via a caller-supplied `onSave({ enabled, values })`.
 *
 * It is reused by BOTH:
 *   - the USER-scoped channels panel (ChannelsPanel) — when `userId` is provided
 *     it additionally renders the read-only auto-prompt block, the
 *     copy-on-enable `custom_prompt` behaviour and the Telegram link widget.
 *   - the ORG-scoped outgoing webhook (IntegrationSettings) — passing only
 *     `config` + `onSave`, with NO userId, so none of the user-only requests
 *     (auto-prompt fetch / telegram status) fire.
 *
 * Field types (config_schema[].type):
 *   string / password / textarea / boolean / events / time / number / weekdays /
 *   nudge_ladder.
 * KAN-764: `weekdays` = 7 checkboxes (ISO 1..7, sorted list), at least one day.
 * KAN-773: `nudge_ladder` = table of {delay_h, lever} steps (1..5, delays
 * strictly increasing); levers come from the schema field `options`.
 * KAN-790: a step may also carry a funnel stage and a step channel (bot message
 * or manager task) when the schema field has `channel_options`; the rules and
 * the payload form live in lib/nudgeLadder.
 * Secrets (type=password) are never seeded into the input; the placeholder
 * reflects `<name>_is_set` in values, and a blank secret is omitted from the
 * payload (no overwrite).
 *
 * TASK-336: the voice channel's dial settings (call_window_start /
 * call_window_end / calls_per_hour) render grouped in a dedicated
 * «Исходящий обзвон» subsection, together with the «Авто-перезвон по
 * договорённостям» toggle (a USER field persisted via the caller-supplied
 * `onAutoCallbackChange`, NOT through the channel values payload). Fields seed
 * from the schema `default` when no value is stored, and `number` values are
 * sent as numbers (the backend validates an int range).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usersApi } from '@/services/api/users';
import { eventsApi } from '@/services/api/events';
import { phoneNumbersApi } from '@/services/api/phoneNumbers';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { WeekdaysPicker } from '@/components/common/WeekdaysPicker';
import { normalizeWeekdays, WEEKDAYS_REQUIRED_ERROR } from '@/lib/weekdays';
import { NudgeLadderEditor } from '@/components/common/NudgeLadderEditor';
import {
  normalizeNudgeLadder,
  nudgeLadderContext,
  nudgeLadderError,
  serializeNudgeLadder,
} from '@/lib/nudgeLadder';
import { useFunnelStageOptions } from '@/components/clients/useFunnelStageOptions';
import type { ChannelField } from '@/types/user';
import { TelegramLinkWidget } from './TelegramLinkWidget';
import { ChannelAdvancedFields } from './ChannelAdvancedFields';
import { clientRouteEnds } from './channelRoute';

const SECRET_TYPE = 'password';
const EVENTS_TYPE = 'events';
const NUMBER_TYPE = 'number';
const TIME_TYPE = 'time';
const WEEKDAYS_TYPE = 'weekdays';
const NUDGE_LADDER_TYPE = 'nudge_ladder';
const TELEGRAM_SYSTEM_CODENAME = 'telegram_system';

// TASK-327: the voice channel's phone number is picked from the org's number
// pool (Настройки, вкладка Телефония) instead of free text input.
const VOICE_CODENAME = 'voice';
const PHONE_NUMBER_FIELD = 'phone_number';
/** Radix Select forbids an empty item value → sentinel for «Не назначен». */
const PHONE_NONE_VALUE = '__none__';

// TASK-336 (FR-001/FR-003): the voice dial-settings schema fields. They render
// grouped in the «Исходящий обзвон» subsection instead of the flat field list.
// The names mirror the backend voice config_schema contract.
const DIAL_SETTINGS_FIELDS = new Set([
  'call_window_start',
  'call_window_end',
  'calls_per_hour',
]);

/**
 * The minimal channel config this section renders. Both `UserChannel` and
 * `OrgChannel` are structurally compatible with it.
 */
export interface ChannelSectionConfig {
  codename: string;
  display_name: string;
  config_schema: ChannelField[];
  enabled: boolean;
  values: Record<string, unknown>;
  /**
   * Channel direction. 'outgoing' = event delivery (Событие → получатель);
   * anything else (incoming / missing) = the bot talks to clients (Клиент → Бот).
   */
  direction?: 'incoming' | 'outgoing';
}

export interface ChannelSectionProps {
  /** The channel catalog entry + stored config to render. */
  channel: ChannelSectionConfig;
  /** Persist the channel; called with the assembled { enabled, values }. */
  onSave: (body: { enabled: boolean; values: Record<string, unknown> }) => Promise<unknown> | void;

  // --- User-channel opt-in features (omit for the generic / org usage) ---
  /**
   * When provided, enables the user-channel features: the runtime auto-prompt
   * block and the Telegram link widget. The org webhook usage omits it, so
   * those requests never fire.
   */
  userId?: string;
  /** Whether the panel is rendered for the current user (self-service). */
  self?: boolean;
  /** TASK-229: edited user's Telegram link status (read-only admin view). */
  telegramLinked?: boolean;
  telegramUsername?: string | null;
  /**
   * TASK-251: map of event codename → human label, used by the outgoing
   * route-header to display configured trigger events. Falls back to the raw
   * codename when an event is not present in the map.
   */
  eventsMap?: Record<string, string>;
  /**
   * TASK-336: initial value of the user's «Авто-перезвон по договорённостям»
   * flag (User.auto_callback_enabled). Only meaningful for the voice channel.
   */
  autoCallbackEnabled?: boolean;
  /**
   * TASK-336: persist the auto-callback flag. It is a USER field (PATCH
   * /users/{id}/), NOT a channel config value, so it deliberately bypasses
   * `onSave`. The toggle renders only when this handler is provided AND the
   * channel is voice.
   */
  onAutoCallbackChange?: (enabled: boolean) => Promise<unknown> | void;
  /**
   * TASK-347: whether the operator's «Имя для клиентов»
   * (User.operator_display_name) is empty. When true, a CLIENT-FACING channel
   * (direction !== 'outgoing' — the bot talks to clients) cannot be enabled:
   * the enable toggle is disabled and a hint tells the operator to set a name
   * first. The outgoing notification channel (direction === 'outgoing') is
   * unaffected. Defaults to false.
   */
  operatorNameMissing?: boolean;
}

/**
 * Derive a generic recipient label for an outgoing channel's route end from its
 * `display_name`. No per-codename hardcode — keyed off well-known transport
 * keywords; otherwise a generic "Получатель".
 */
function deriveRecipient(displayName: string): string {
  const name = displayName.toLowerCase();
  if (name.includes('email') || name.includes('почт') || name.includes('mail')) {
    return '📧 Email';
  }
  if (name.includes('telegram') || name.includes('телеграм')) {
    return '📲 Telegram';
  }
  if (name.includes('webhook') || name.includes('вебхук') || name.includes('http')) {
    return '🌐 Webhook';
  }
  return 'Получатель';
}

/**
 * Whether the channel satisfies all its REQUIRED config fields. Generic over the
 * schema (no per-codename logic): a password field is satisfied by a stored
 * `<name>_is_set` flag OR a non-empty value; any other field by a present,
 * non-empty value (and, for arrays, a non-empty array). `required: false`
 * fields (e.g. `events`) never gate.
 */
function isChannelConfigured(
  configSchema: ChannelField[],
  values: Record<string, unknown>
): boolean {
  for (const field of configSchema) {
    if (!field.required) continue;

    if (field.type === SECRET_TYPE) {
      const isSet = values?.[`${field.name}_is_set`] === true;
      const typed = values?.[field.name];
      const hasValue = typeof typed === 'string' && typed.trim() !== '';
      if (!isSet && !hasValue) return false;
      continue;
    }

    const value = values?.[field.name];
    if (Array.isArray(value)) {
      if (value.length === 0) return false;
    } else if (value == null || (typeof value === 'string' && value.trim() === '')) {
      return false;
    }
  }
  return true;
}

/**
 * `events`-type field: a multiselect of checkboxes, one per available event.
 * Isolated into its own component so the `useQuery` hook obeys the rules of
 * hooks (it cannot live inside ChannelSection's `renderField` closure).
 */
interface EventsFieldProps {
  codename: string;
  selected: string[];
  onToggle: (eventCodename: string, checked: boolean) => void;
}

function EventsField({ codename, selected, onToggle }: EventsFieldProps): JSX.Element {
  const { data: events, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: eventsApi.list,
  });

  return (
    <div data-testid={`channel-field-${codename}-events`} className="space-y-2">
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Загрузка событий...</span>
        </div>
      )}
      {(events ?? []).map((event) => {
        const checked = selected.includes(event.codename);
        return (
          <label
            key={event.codename}
            className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              data-testid={`event-option-${event.codename}`}
              aria-label={event.label}
              checked={checked}
              onCheckedChange={(next) => onToggle(event.codename, next)}
            />
            <span>{event.label}</span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Normalize a phone number to a digits-only form for comparison (LOW-10),
 * mirroring the backend `to_e164` rules: strip everything but digits,
 * `8XXXXXXXXXX` → `7XXXXXXXXXX`, a bare 10-digit number gets the `7` prefix.
 * Comparison only — the raw stored/pool values are never rewritten.
 */
function normalizePhoneDigits(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    digits = `7${digits.slice(1)}`;
  }
  if (digits.length === 10) {
    digits = `7${digits}`;
  }
  return digits;
}

/**
 * TASK-327: voice-channel `phone_number` field — a select over the ACTIVE
 * numbers of the organization pool (GET /api/v1/phone-numbers/) plus a
 * «Не назначен» item mapped to the empty value. A legacy value stored before
 * the pool existed is kept selectable (labelled «вне пула») so reading old
 * configs never breaks (AC-5). Numbers assigned to ANOTHER operator are shown
 * disabled with the holder's name (the backend rejects such saves with 400),
 * while free numbers and the edited user's own number stay selectable.
 * Isolated into its own component so the `useQuery` hook obeys the rules of
 * hooks (cannot live inside `renderField`).
 */
interface PhonePoolFieldProps {
  fieldId: string;
  testId: string;
  value: string;
  onChange: (next: string) => void;
  /** The user whose channel is edited — their own number stays selectable. */
  userId?: string;
}

function PhonePoolField({
  fieldId,
  testId,
  value,
  onChange,
  userId,
}: PhonePoolFieldProps): JSX.Element {
  const { data: pool, isLoading } = useQuery({
    queryKey: ['phone-numbers'],
    queryFn: phoneNumbersApi.list,
  });

  const activeNumbers = (pool ?? []).filter(
    (n) => n.status === 'active' && n.phone_number !== ''
  );
  // Match the stored value against the pool on normalized digits, so format
  // differences («+74951234567» vs «74951234567») are not misread as legacy.
  const normalizedValue = normalizePhoneDigits(value);
  const matchedPoolNumber =
    value !== ''
      ? activeNumbers.find(
          (n) => normalizePhoneDigits(n.phone_number) === normalizedValue
        )
      : undefined;
  // Legacy number saved before the pool existed → keep it displayable.
  const isLegacy = value !== '' && matchedPoolNumber == null;
  // Drive the Select with the pool's canonical spelling when it matched.
  const selectValue =
    value === '' ? PHONE_NONE_VALUE : (matchedPoolNumber?.phone_number ?? value);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Загрузка номеров...</span>
      </div>
    );
  }

  return (
    <Select
      value={selectValue}
      onValueChange={(next) => onChange(next === PHONE_NONE_VALUE ? '' : next)}>
      <SelectTrigger id={fieldId} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={PHONE_NONE_VALUE}>Не назначен</SelectItem>
        {isLegacy && (
          <SelectItem value={value}>{value} (вне пула)</SelectItem>
        )}
        {activeNumbers.map((n) => {
          // Taken by another operator → visible, but not selectable: the
          // backend rejects assigning an occupied number with 400.
          const takenByOther =
            n.assigned_to != null && n.assigned_to.id !== userId;
          return (
            <SelectItem
              key={n.id}
              value={n.phone_number}
              disabled={takenByOther}>
              {n.phone_number}
              {n.assigned_to ? ` — ${n.assigned_to.display_name}` : ''}
              {takenByOther ? ' (занят)' : ''}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

/**
 * One isolated channel block. Collapsible (collapsed by default).
 */
export function ChannelSection({
  channel,
  onSave,
  userId,
  self = false,
  telegramLinked,
  telegramUsername,
  eventsMap,
  autoCallbackEnabled,
  onAutoCallbackChange,
  operatorNameMissing = false,
}: ChannelSectionProps): JSX.Element {
  const { codename, display_name, config_schema, values: initialValues } = channel;

  // Route header data — derived purely from `direction` + schema/values.
  const outgoing = channel.direction === 'outgoing';

  // TASK-347: a CLIENT-FACING channel (the bot talks to clients — anything but
  // `outgoing`) cannot be enabled while the operator's «Имя для клиентов» is
  // empty. The outgoing notification channel is never blocked.
  const nameBlocksEnable = operatorNameMissing && !outgoing;

  // User-channel features are opt-in: only when a userId is supplied.
  const isUserChannel = userId != null;

  // Expander state — collapsed by default.
  const [expanded, setExpanded] = useState(false);

  // Channel on/off switch (seeded from channel.enabled).
  const [enabled, setEnabled] = useState<boolean>(channel.enabled);

  // Field values seeded from channel.values; secrets stay empty, events arrays.
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {};
    for (const field of config_schema) {
      if (field.type === SECRET_TYPE) {
        // Never seed a secret into the editable input.
        seed[field.name] = '';
      } else if (field.type === EVENTS_TYPE) {
        const stored = initialValues?.[field.name];
        seed[field.name] = Array.isArray(stored) ? [...stored] : [];
      } else if (field.type === WEEKDAYS_TYPE) {
        seed[field.name] =
          normalizeWeekdays(initialValues?.[field.name]) ?? normalizeWeekdays(field.default) ?? [];
      } else if (field.type === NUDGE_LADDER_TYPE) {
        const options = field.options ?? [];
        seed[field.name] =
          normalizeNudgeLadder(initialValues?.[field.name], options) ??
          normalizeNudgeLadder(field.default, options) ??
          [];
      } else {
        // TASK-336: fall back to the schema `default` (e.g. dial-settings
        // fields) so untouched fields save the default, not an empty string.
        seed[field.name] = initialValues?.[field.name] ?? field.default ?? '';
      }
    }
    return seed;
  });

  const [isSaving, setIsSaving] = useState(false);
  // KAN-764: weekdays fields left with no day selected block the save.
  const [weekdaysError, setWeekdaysError] = useState<string | null>(null);
  // KAN-773: an invalid nudge ladder (empty, >5 steps, non-increasing delays)
  // blocks the save; the text comes from nudgeLadderError().
  const [ladderError, setLadderError] = useState<string | null>(null);
  // KAN-790: funnel stages for the ladder steps, loaded on first expand and
  // only when the schema field offers step channels (newer backend).
  const hasStagedLadder = config_schema.some(
    (f) => f.type === NUDGE_LADDER_TYPE && (f.channel_options?.length ?? 0) > 0
  );
  const ladderStages = useFunnelStageOptions(expanded && hasStagedLadder);

  // TASK-336: voice-only «Исходящий обзвон» subsection. Dial-settings schema
  // fields render grouped there; every other field stays in the flat list.
  const isVoice = codename === VOICE_CODENAME;
  const dialFields = isVoice
    ? config_schema.filter((f) => DIAL_SETTINGS_FIELDS.has(f.name))
    : [];
  const listFields = isVoice
    ? config_schema.filter((f) => !DIAL_SETTINGS_FIELDS.has(f.name))
    : config_schema;
  // KAN-849: schema fields flagged `advanced` render in a collapsed block.
  // Only the layout changes: the payload is still assembled from the whole
  // schema, so their values are sent on every save.
  const regularFields = listFields.filter((f) => !f.advanced);
  const advancedFields = listFields.filter((f) => f.advanced);
  const [routeFrom, routeTo] = clientRouteEnds(codename);

  // Auto-callback toggle — a USER field, persisted through the dedicated
  // handler (NOT through the channel values payload). Optimistic local state,
  // reverted (with an error toast) if the PATCH fails.
  const showAutoCallback = isVoice && onAutoCallbackChange != null;
  const [autoCallback, setAutoCallback] = useState<boolean>(
    Boolean(autoCallbackEnabled)
  );
  // TASK-336 review №8: resync the optimistic state whenever the prop changes
  // (e.g. ProfilePage refetches the user and passes a fresh value down).
  useEffect(() => {
    setAutoCallback(Boolean(autoCallbackEnabled));
  }, [autoCallbackEnabled]);
  const handleAutoCallbackToggle = useCallback(
    (checked: boolean) => {
      setAutoCallback(checked);
      Promise.resolve(onAutoCallbackChange?.(checked)).catch(() => {
        // Functional revert — never a stale closure over `checked`, so rapid
        // consecutive toggles roll back from the CURRENT state.
        setAutoCallback((prev) => !prev);
        toast.error('Не удалось сохранить настройку авто-перезвона');
      });
    },
    [onAutoCallbackChange]
  );

  // Route-header summary reflects the LIVE section state (`fieldValues`), so
  // editing events / fields updates the route header and the "не настроен" flag
  // immediately — not only after a reload. Secrets are intentionally blank in
  // `fieldValues`, so the stored `<name>_is_set` flags are carried over from
  // `initialValues` to keep `isChannelConfigured`'s password check correct.
  // When collapsed, `fieldValues` is still seeded from `initialValues`, so this
  // matches the previous behaviour.
  const liveValues = useMemo(
    () => ({ ...(initialValues ?? {}), ...fieldValues }),
    [initialValues, fieldValues]
  );
  const configured = isChannelConfigured(config_schema, liveValues);
  // Selected trigger events come from the live `events` field value.
  const eventsField = config_schema.find((f) => f.type === EVENTS_TYPE);
  const selectedEvents: string[] =
    eventsField && Array.isArray(fieldValues[eventsField.name])
      ? (fieldValues[eventsField.name] as string[])
      : [];

  // Fetch the runtime auto-prompt for this channel (user channels only).
  // Deferred until first expanded so collapsed sections don't fire a request.
  const { data: autoPromptData } = useQuery({
    queryKey: ['channel-auto-prompt', userId, codename],
    queryFn: () => usersApi.getChannelAutoPrompt(userId as string, codename),
    enabled: isUserChannel && expanded,
  });
  const autoPrompt = autoPromptData?.prompt ?? '';

  const hasCustomPrompt = config_schema.some((f) => f.name === 'custom_prompt');
  const isTelegramSystem = isUserChannel && codename === TELEGRAM_SYSTEM_CODENAME;

  const setField = useCallback((name: string, value: unknown) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  /** Toggle a single event in an events-field array immutably. */
  const toggleEvent = useCallback((name: string, eventCodename: string, checked: boolean) => {
    setFieldValues((prev) => {
      const current = Array.isArray(prev[name]) ? (prev[name] as string[]) : [];
      const next = checked
        ? current.includes(eventCodename)
          ? current
          : [...current, eventCodename]
        : current.filter((c) => c !== eventCodename);
      return { ...prev, [name]: next };
    });
  }, []);

  /**
   * Toggling "use custom prompt" ON: if the custom_prompt textarea is empty,
   * fill it with a COPY of the loaded auto-prompt. Non-empty → leave as-is.
   */
  const handleCustomEnabledChange = useCallback(
    (checked: boolean) => {
      setFieldValues((prev) => {
        const next: Record<string, unknown> = { ...prev, custom_prompt_enabled: checked };
        if (checked) {
          const current = (prev.custom_prompt as string) ?? '';
          if (current.trim() === '' && autoPrompt) {
            next.custom_prompt = autoPrompt;
          }
        }
        return next;
      });
    },
    [autoPrompt]
  );

  // Assemble the values payload from the schema. Shared by both the explicit
  // "Сохранить канал" button and the header enable toggle so each sends an
  // identical `values` body.
  const assemblePayloadValues = useCallback((): Record<string, unknown> => {
    const payloadValues: Record<string, unknown> = {};
    for (const field of config_schema) {
      if (field.type === SECRET_TYPE) {
        const typed = (fieldValues[field.name] as string) ?? '';
        if (typed !== '') {
          payloadValues[field.name] = typed;
        }
        // Untouched/empty secret → omit (no overwrite).
      } else if (field.type === NUMBER_TYPE) {
        // TASK-336: the backend validates a NUMBER (e.g. int 1..120), so the
        // payload value must never be a string. Empty/garbage input falls
        // back to the schema default; without one the field is omitted.
        const raw = fieldValues[field.name];
        const num = typeof raw === 'number' ? raw : Number(raw);
        if (raw !== '' && raw != null && Number.isFinite(num)) {
          payloadValues[field.name] = num;
        } else if (field.default !== undefined) {
          payloadValues[field.name] = field.default;
        }
      } else if (field.type === TIME_TYPE) {
        // A cleared time input falls back to the schema default (never send
        // an empty window boundary when the schema defines one).
        const raw = fieldValues[field.name];
        payloadValues[field.name] =
          raw != null && raw !== '' ? raw : (field.default ?? '');
      } else if (field.type === NUDGE_LADDER_TYPE) {
        // KAN-790: the backend form (a plain bot step stays {delay_h, lever}).
        payloadValues[field.name] = serializeNudgeLadder(
          normalizeNudgeLadder(fieldValues[field.name], field.options ?? []) ?? [],
          field.channel_options ?? []
        );
      } else {
        payloadValues[field.name] = fieldValues[field.name];
      }
    }
    return payloadValues;
  }, [config_schema, fieldValues]);

  const hasEmptyWeekdays = useCallback(
    () =>
      config_schema.some(
        (f) => f.type === WEEKDAYS_TYPE && (normalizeWeekdays(fieldValues[f.name]) ?? []).length === 0
      ),
    [config_schema, fieldValues]
  );

  /** KAN-773: first ladder error across nudge_ladder fields, or null. */
  const findLadderError = useCallback((): string | null => {
    for (const f of config_schema) {
      if (f.type !== NUDGE_LADDER_TYPE) continue;
      const err = nudgeLadderError(
        normalizeNudgeLadder(fieldValues[f.name], f.options ?? []) ?? [],
        nudgeLadderContext(f, ladderStages)
      );
      if (err) return err;
    }
    return null;
  }, [config_schema, fieldValues, ladderStages]);

  const handleSave = useCallback(() => {
    if (hasEmptyWeekdays()) {
      setWeekdaysError(WEEKDAYS_REQUIRED_ERROR);
      return;
    }
    setWeekdaysError(null);
    const ladderErr = findLadderError();
    if (ladderErr) {
      setLadderError(ladderErr);
      return;
    }
    setLadderError(null);
    setIsSaving(true);
    // TASK-347 (AC-2): surface the backend's Russian error text in a toast when
    // the save is rejected (e.g. 400 «Укажите имя для клиентов … перед
    // включением канала») instead of failing silently.
    Promise.resolve(onSave({ enabled, values: assemblePayloadValues() }))
      .catch((err: unknown) => {
        toast.error(extractErrorMessage(err, 'Не удалось сохранить канал'));
      })
      .finally(() => setIsSaving(false));
  }, [onSave, enabled, assemblePayloadValues, hasEmptyWeekdays, findLadderError]);

  // Toggling the header enable switch persists immediately (sends a request),
  // rather than waiting for the explicit save button.
  const handleToggleEnabled = useCallback(
    (checked: boolean) => {
      setEnabled(checked);
      setIsSaving(true);
      Promise.resolve(
        onSave({ enabled: checked, values: assemblePayloadValues() })
      )
        .catch((err: unknown) => {
          // Revert the optimistic switch and show the backend's message.
          setEnabled((prev) => !prev);
          toast.error(extractErrorMessage(err, 'Не удалось сохранить канал'));
        })
        .finally(() => setIsSaving(false));
    },
    [onSave, assemblePayloadValues]
  );

  const renderField = (field: ChannelField): JSX.Element | null => {
    const testId = `channel-field-${codename}-${field.name}`;
    const fieldId = `${codename}-${field.name}`;

    // TASK-327: the voice phone number comes from the org pool, not free text.
    if (codename === VOICE_CODENAME && field.name === PHONE_NUMBER_FIELD) {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <PhonePoolField
            fieldId={fieldId}
            testId={testId}
            value={(fieldValues[field.name] as string) ?? ''}
            onChange={(next) => setField(field.name, next)}
            userId={userId}
          />
          <p className="text-xs text-muted-foreground">
            Номер выбирается из активных номеров пула организации (Настройки,
            вкладка Телефония).
          </p>
        </div>
      );
    }

    // The "use custom prompt" boolean has special copy-on-enable behaviour.
    if (field.name === 'custom_prompt_enabled' && field.type === 'boolean') {
      return (
        <div key={field.name} className="flex items-center justify-between gap-2">
          <Label htmlFor={fieldId}>{field.label}</Label>
          <Switch
            id={fieldId}
            data-testid={testId}
            aria-label={field.label}
            checked={Boolean(fieldValues.custom_prompt_enabled)}
            onCheckedChange={handleCustomEnabledChange}
          />
        </div>
      );
    }

    switch (field.type) {
      case 'boolean':
        return (
          <div key={field.name} className="flex items-center justify-between gap-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <Switch
              id={fieldId}
              data-testid={testId}
              aria-label={field.label}
              checked={Boolean(fieldValues[field.name])}
              onCheckedChange={(checked) => setField(field.name, checked)}
            />
          </div>
        );

      case EVENTS_TYPE: {
        const selected = Array.isArray(fieldValues[field.name])
          ? (fieldValues[field.name] as string[])
          : [];
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <EventsField
              codename={codename}
              selected={selected}
              onToggle={(eventCodename, checked) =>
                toggleEvent(field.name, eventCodename, checked)
              }
            />
          </div>
        );
      }

      case SECRET_TYPE: {
        const isSet = initialValues?.[`${field.name}_is_set`] === true;
        const placeholder = isSet
          ? `${field.label} задан — оставьте пустым, чтобы не менять`
          : `Введите ${field.label.toLowerCase()}`;
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <PasswordInput
              id={fieldId}
              data-testid={testId}
              value={(fieldValues[field.name] as string) ?? ''}
              placeholder={placeholder}
              onChange={(e) => setField(field.name, e.target.value)}
            />
          </div>
        );
      }

      case TIME_TYPE:
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <Input
              id={fieldId}
              data-testid={testId}
              type="time"
              value={(fieldValues[field.name] as string) ?? ''}
              onChange={(e) => setField(field.name, e.target.value)}
            />
          </div>
        );

      case WEEKDAYS_TYPE:
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <WeekdaysPicker
              idPrefix={fieldId}
              testId={testId}
              value={normalizeWeekdays(fieldValues[field.name]) ?? []}
              onChange={(days) => {
                setField(field.name, days);
                if (days.length > 0) setWeekdaysError(null);
              }}
            />
            {weekdaysError && (
              <p role="alert" className="text-sm text-red-600">
                {weekdaysError}
              </p>
            )}
          </div>
        );

      case NUDGE_LADDER_TYPE: {
        const ladder = nudgeLadderContext(field, ladderStages);
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            <NudgeLadderEditor
              idPrefix={fieldId}
              testId={testId}
              options={ladder.options}
              channelOptions={ladder.channelOptions}
              stageOptions={ladder.stageOptions}
              value={normalizeNudgeLadder(fieldValues[field.name], ladder.options) ?? []}
              onChange={(steps) => {
                setField(field.name, steps);
                // Clear the error as soon as the ladder becomes valid again.
                if (nudgeLadderError(steps, ladder) === null) setLadderError(null);
              }}
              error={ladderError}
            />
          </div>
        );
      }

      case NUMBER_TYPE: {
        const raw = fieldValues[field.name];
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <Input
              id={fieldId}
              data-testid={testId}
              type="number"
              min={field.min}
              max={field.max}
              value={raw == null ? '' : String(raw)}
              onChange={(e) =>
                // Keep the state numeric so the save payload sends a number.
                setField(
                  field.name,
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
            />
          </div>
        );
      }

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <Textarea
              id={fieldId}
              data-testid={testId}
              rows={6}
              className="max-h-48 overflow-y-auto resize-y"
              value={(fieldValues[field.name] as string) ?? ''}
              onChange={(e) => setField(field.name, e.target.value)}
            />
          </div>
        );

      case 'string':
      default:
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={fieldId}>{field.label}</Label>
            <Input
              id={fieldId}
              data-testid={testId}
              type="text"
              value={(fieldValues[field.name] as string) ?? ''}
              onChange={(e) => setField(field.name, e.target.value)}
            />
          </div>
        );
    }
  };

  return (
    <section
      data-testid={`channel-section-${codename}`}
      className="rounded-lg border border-border">
      {/* Header: expander toggle + channel enable switch */}
      <div className="flex items-center justify-between gap-2 p-4">
        <button
          type="button"
          data-testid={`channel-toggle-${codename}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left">
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <h3 className="text-sm font-semibold">{display_name}</h3>
          {!expanded && (
            <Badge variant={enabled ? 'completed' : 'secondary'} className="ml-1">
              {enabled ? 'вкл' : 'выкл'}
            </Badge>
          )}
        </button>
        {/* Enable switch lives outside the toggle button so it never toggles it. */}
        <Switch
          data-testid={`channel-enable-${codename}`}
          aria-label={`Включить ${display_name}`}
          checked={enabled}
          disabled={isSaving || nameBlocksEnable}
          onCheckedChange={handleToggleEnabled}
        />
      </div>

      {/* TASK-347: hint shown when a client-facing channel is blocked from being
          enabled because the operator's «Имя для клиентов» is empty. */}
      {nameBlocksEnable && (
        <div
          data-testid={`channel-name-required-${codename}`}
          className="px-4 pb-2 text-xs text-amber-800">
          Сначала укажите имя для клиентов
        </div>
      )}

      {/* Route header — two labelled ends + arrow, generic from `direction`. */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 text-xs text-muted-foreground">
        <div
          data-testid={`channel-route-${codename}`}
          className="flex flex-wrap items-center gap-2">
          {outgoing ? (
            <>
              <span className="rounded bg-muted px-2 py-0.5">Событие</span>
              {selectedEvents.length > 0 ? (
                <span className="text-foreground">
                  {selectedEvents
                    .map((code) => eventsMap?.[code] ?? code)
                    .join(', ')}
                </span>
              ) : (
                <span className="italic">События не выбраны</span>
              )}
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              <span className="rounded bg-muted px-2 py-0.5">
                {deriveRecipient(display_name)}
              </span>
            </>
          ) : (
            <>
              <span className="rounded bg-muted px-2 py-0.5">{routeFrom}</span>
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              <span className="rounded bg-muted px-2 py-0.5">{routeTo}</span>
            </>
          )}
        </div>
        {!configured && (
          <span
            data-testid={`channel-unconfigured-${codename}`}
            className="rounded bg-amber-100 px-2 py-0.5 text-amber-800">
            ⚠ не настроен
          </span>
        )}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="space-y-4 border-t border-border p-4">
          {/* Auto-prompt (read-only) for user channels that support a custom prompt */}
          {isUserChannel && hasCustomPrompt && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Авто-промпт (из рантайма)</Label>
                <Badge variant="secondary" className="text-[10px] font-normal">
                  только чтение
                </Badge>
              </div>
              <div
                data-testid={`channel-auto-prompt-${codename}`}
                aria-readonly="true"
                className="max-h-48 cursor-not-allowed overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
                {autoPrompt}
              </div>
              <p className="text-xs text-muted-foreground">
                Кастомный промпт имеет приоритет и используется вместо авто-промпта.
              </p>
            </div>
          )}

          {/* TASK-253: composition hint for the channel custom prompt. The
              custom prompt sets the channel's РОЛЬ / СТИЛЬ / медиум; the
              funnel-stage ЦЕЛЬ / ТАКТИКА (if enabled) is layered ON TOP
              automatically, so the operator must not duplicate it here. */}
          {hasCustomPrompt && (
            <p
              data-testid={`channel-custom-prompt-hint-${codename}`}
              className="text-xs text-muted-foreground">
              Здесь — РОЛЬ / СТИЛЬ / медиум канала. ЦЕЛЬ и ТАКТИКА под стадии
              воронки (если включена) добавляются поверх автоматически — не
              дублируйте их здесь.
            </p>
          )}

          {/* Dynamic fields from config_schema (voice dial settings render
              grouped in their own subsection below) */}
          <div className="space-y-4">{regularFields.map((field) => renderField(field))}</div>

          {advancedFields.length > 0 && (
            <ChannelAdvancedFields codename={codename}>
              {advancedFields.map((field) => renderField(field))}
            </ChannelAdvancedFields>
          )}

          {/* TASK-336: voice «Исходящий обзвон» — call window, calls-per-hour
              rate and the auto-callback toggle, grouped in one subsection. */}
          {isVoice && (dialFields.length > 0 || showAutoCallback) && (
            <div
              data-testid={`channel-dial-settings-${codename}`}
              className="space-y-4 rounded-md border border-border p-3">
              <h4 className="text-sm font-semibold">Исходящий обзвон</h4>
              {dialFields.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-3">
                  {dialFields.map((field) => renderField(field))}
                </div>
              )}
              {showAutoCallback && (
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <Label htmlFor={`${codename}-auto-callback`}>
                      Авто-перезвон по договорённостям
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Бот сам перезвонит клиенту в согласованное время.
                    </p>
                  </div>
                  <Switch
                    id={`${codename}-auto-callback`}
                    data-testid="auto-callback-switch"
                    aria-label="Авто-перезвон по договорённостям"
                    checked={autoCallback}
                    onCheckedChange={handleAutoCallbackToggle}
                  />
                </div>
              )}
            </div>
          )}

          {/* Telegram account link widget (telegram_system user channel only) */}
          {isTelegramSystem && (
            <TelegramLinkWidget
              self={self}
              linked={telegramLinked}
              username={telegramUsername}
            />
          )}

          {/* Per-channel save button */}
          <div className="flex justify-end">
            <Button
              type="button"
              data-testid={`channel-save-${codename}`}
              onClick={handleSave}
              disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить канал
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default ChannelSection;
